import React, { useEffect, useMemo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Tooltip } from 'antd';
import {
  ApiOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  ExclamationCircleOutlined,
  ThunderboltFilled
} from '@ant-design/icons';
import {
  useBookstatDataFlow,
  useDashboardCards
} from '../../../core/modules/pipelines/hooks';
import {
  IPipelineSummary,
  PipelineStatus
} from '../../../core/modules/pipelines/pipelines.model';
import { IFlowJob } from '../../../core/modules/pipelines/pipelines.dto';
import { BRENDA_GREEN } from '../utils/statusColors';
import { formatRelative } from '../utils/formatStage';

const INFERENCE_ID = 'durable.inference.inference_orchestrator';
const BACKEND_ID = 'function.backend.get_backend_data';

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const dotTravel = keyframes`
  0%   { left: 0%;  opacity: 0; }
  10%  { opacity: 1; }
  90%  { opacity: 1; }
  100% { left: calc(100% - 8px); opacity: 0; }
`;

const pulseHalo = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 currentColor; opacity: 1; }
  50%      { box-shadow: 0 0 0 6px currentColor; opacity: 0.35; }
`;

const shimmer = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

// ---------------------------------------------------------------------------
// Layout primitives
// ---------------------------------------------------------------------------

const Wrapper = styled.section`
  background: linear-gradient(135deg, #ffffff 0%, #fafffd 100%);
  border: 1px solid #ececec;
  border-radius: 14px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  display: inline-flex;
  align-items: center;
  gap: 8px;

  & .anticon {
    color: ${BRENDA_GREEN};
    font-size: 16px;
  }
`;

const TitleHint = styled.span`
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  letter-spacing: 0.2px;
`;

const FreshnessPill = styled.div<{ $tone: 'ok' | 'warn' | 'bad' | 'muted' }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px 5px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  ${({ $tone }) => {
    switch ($tone) {
    case 'warn':
      return css`background: #fff7e6; color: #d48806; border: 1px solid #ffe7ba;`;
    case 'bad':
      return css`background: #fff1f0; color: #cf1322; border: 1px solid #ffccc7;`;
    case 'muted':
      return css`background: #fafafa; color: rgba(0,0,0,0.5); border: 1px solid #e8e8e8;`;
    default:
      return css`background: ${BRENDA_GREEN}14; color: ${BRENDA_GREEN}; border: 1px solid ${BRENDA_GREEN}40;`;
    }
  }}
`;

const FreshnessDot = styled.span<{ $color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  color: ${({ $color }) => $color};
  animation: ${pulseHalo} 2.2s ease-in-out infinite;
`;

// ---------------------------------------------------------------------------
// Flow lane
// ---------------------------------------------------------------------------

const FlowRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 64px 1fr;
  align-items: stretch;
  gap: 0;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const Card = styled.div<{ $accent: string; $running?: boolean }>`
  position: relative;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: ${({ $accent }) => $accent};
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  }

  ${({ $running, $accent }) =>
    $running &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 12px;
        background: linear-gradient(90deg, transparent 25%, ${$accent}10 50%, transparent 75%);
        background-size: 200% 100%;
        animation: ${shimmer} 2.4s linear infinite;
        pointer-events: none;
      }
    `}
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CardTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`;

const IconChip = styled.span<{ $accent: string }>`
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: ${({ $accent }) => `${$accent}1a`};
  color: ${({ $accent }) => $accent};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
`;

const StatusPill = styled.span<{ $tone: 'ok' | 'warn' | 'bad' | 'muted' | 'live' }>`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: 999px;
  ${({ $tone }) => {
    switch ($tone) {
    case 'warn':
      return css`background: #fff7e6; color: #d48806;`;
    case 'bad':
      return css`background: #fff1f0; color: #cf1322;`;
    case 'muted':
      return css`background: #fafafa; color: rgba(0,0,0,0.5);`;
    case 'live':
      return css`background: ${BRENDA_GREEN}1a; color: ${BRENDA_GREEN};`;
    default:
      return css`background: ${BRENDA_GREEN}1a; color: ${BRENDA_GREEN};`;
    }
  }}
`;

const HeroValue = styled.div<{ $tone: 'ok' | 'warn' | 'bad' | 'muted' }>`
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.2px;
  ${({ $tone }) => {
    switch ($tone) {
    case 'warn':
      return css`color: #d48806;`;
    case 'bad':
      return css`color: #cf1322;`;
    case 'muted':
      return css`color: rgba(0,0,0,0.55);`;
    default:
      return css`color: rgba(0,0,0,0.88);`;
    }
  }}
`;

const HeroLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(0, 0, 0, 0.4);
  display: block;
  margin-bottom: 2px;
`;

const LagChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  background: #fff7e6;
  color: #d48806;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 10px;
`;

const StatStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  border-top: 1px dashed #efefef;
  padding-top: 10px;
  margin-top: 2px;
`;

const StatTile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const StatLabel = styled.span`
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.4);

  & .anticon {
    margin-right: 4px;
    font-size: 10px;
    color: rgba(0, 0, 0, 0.35);
  }
`;

const StatValue = styled.span<{ $tone?: 'warn' | 'bad' | 'muted' }>`
  font-size: 12px;
  font-weight: 600;
  color: ${({ $tone }) =>
    $tone === 'warn'
      ? '#d48806'
      : $tone === 'bad'
        ? '#cf1322'
        : $tone === 'muted'
          ? 'rgba(0,0,0,0.45)'
          : 'rgba(0,0,0,0.82)'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ---------------------------------------------------------------------------
// Connector
// ---------------------------------------------------------------------------

const ConnectorSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;

  @media (max-width: 920px) {
    transform: rotate(90deg);
    padding: 6px 0;
  }
`;

const Rail = styled.div<{ $accent: string }>`
  position: relative;
  width: 100%;
  height: 2px;
  background: linear-gradient(
    90deg,
    ${({ $accent }) => `${$accent}33`},
    ${({ $accent }) => `${$accent}`}
  );
  border-radius: 2px;
`;

const TravelDot = styled.span<{ $accent: string; $duration: string }>`
  position: absolute;
  top: 50%;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $accent }) => $accent};
  box-shadow: 0 0 8px ${({ $accent }) => $accent};
  transform: translateY(-50%);
  animation: ${dotTravel} ${({ $duration }) => $duration} ease-in-out infinite;
`;

const Chevron = styled.span<{ $accent: string }>`
  margin-left: 6px;
  color: ${({ $accent }) => $accent};
  font-size: 12px;
  display: inline-flex;
  align-items: center;
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_COLOR: Record<PipelineStatus, string> = {
  running: '#1890ff',
  pending: '#8c8c8c',
  success: BRENDA_GREEN,
  failed: '#cf1322',
  cancelled: '#bfbfbf',
  warning: '#faad14',
  unknown: '#bfbfbf'
};

const formatDateOnly = (iso: string | null | undefined): string => {
  if (!iso) {
    return '—';
  }

  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) {
    return iso.slice(0, 10);
  }

  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const daysBetween = (a?: string | null, b?: string | null): number | null => {
  if (!a || !b) {
    return null;
  }

  const da = new Date(a);
  const db = new Date(b);

  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) {
    return null;
  }

  return Math.round((da.getTime() - db.getTime()) / 86_400_000);
};

const formatDurationCompact = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) {
    return '—';
  }

  if (seconds < 60) {
    return `${seconds}s`;
  }

  const m = Math.floor(seconds / 60);
  const s = seconds % 60;

  if (m < 60) {
    return s ? `${m}m ${s}s` : `${m}m`;
  }

  const h = Math.floor(m / 60);
  const rem = m % 60;

  return rem ? `${h}h ${rem}m` : `${h}h`;
};

const formatCountdown = (target: string | null | undefined, now: number): string => {
  if (!target) {
    return '—';
  }

  const t = new Date(target).getTime();

  if (Number.isNaN(t)) {
    return '—';
  }

  const diff = Math.max(0, t - now);
  const m = Math.floor(diff / 60_000);

  if (m < 1) {
    return '<1m';
  }

  if (m < 60) {
    return `${m}m`;
  }

  const h = Math.floor(m / 60);
  const rem = m % 60;

  return rem ? `${h}h ${rem}m` : `${h}h`;
};

type Tone = 'ok' | 'warn' | 'bad' | 'muted';

const statusToTone = (status: PipelineStatus, lagDays: number | null): Tone => {
  if (status === 'failed') {
    return 'bad';
  }

  if (status === 'warning' || (lagDays !== null && lagDays > 1)) {
    return 'warn';
  }

  if (status === 'unknown') {
    return 'muted';
  }

  return 'ok';
};

const statusToBadge = (status: PipelineStatus): string => {
  if (status === 'running') {
    return 'Running';
  }

  if (status === 'failed') {
    return 'Failed';
  }

  if (status === 'warning') {
    return 'Warning';
  }

  if (status === 'unknown') {
    return 'No data';
  }

  return 'Healthy';
};

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

const TooltipContent = styled.div<{
  $palette: { bg: string; border: string; text: string; icon: string };
}>`
  padding: 10px 14px;
  color: ${({ $palette }) => $palette.text};
  font-size: 12.5px;
  line-height: 1.5;
  max-width: 420px;
`;

const TooltipHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const TooltipBody = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
`;

interface IFlowCardProps {
  title: string;
  icon: React.ReactNode;
  summary?: IPipelineSummary;
  job?: IFlowJob | null;
  now: number;
  lagDays?: number | null;
  uiOnly?: boolean;
  nextEtaOverride?: string | null;
  nextEtaLabel?: string;
}

const FlowCard: React.FC<IFlowCardProps> = ({
  title, icon, summary, job, now, lagDays, uiOnly,
  nextEtaOverride, nextEtaLabel
}) => {
  const status = summary?.latestRunStatus ?? (uiOnly ? 'success' : 'unknown');
  const color = STATUS_COLOR[status];
  const isRunning = status === 'running' || status === 'pending';
  const hoverMessage = !uiOnly ? summary?.errorMessage : null;

  const tone: Tone = uiOnly ? 'ok' : statusToTone(status, lagDays ?? null);
  const badge = uiOnly ? 'Live' : statusToBadge(status);
  const pillTone: 'ok' | 'warn' | 'bad' | 'muted' | 'live' =
    uiOnly ? 'live' : tone;

  const heroLabel = uiOnly ? 'Showing as of' : 'Data as of';

  const card = (
    <Card $accent={color} $running={isRunning}>
      <CardHead>
        <CardTitle>
          <IconChip $accent={color}>{icon}</IconChip>
          {title}
        </CardTitle>
        <StatusPill $tone={pillTone}>{badge}</StatusPill>
      </CardHead>

      <div>
        <HeroLabel>{heroLabel}</HeroLabel>
        <HeroValue $tone={tone}>{formatDateOnly(job?.bookstatDate)}</HeroValue>
      </div>

      {!uiOnly && (() => {
        const effective = nextEtaOverride !== undefined
          ? nextEtaOverride
          : summary?.nextExpectedAt ?? null;
        const showCountdown = !(nextEtaOverride === null);

        // ETA when running: projected finish = startedAt + avgDuration.
        // Falls back to scheduled-next when no avg history available.
        const runningEtaSec = (() => {
          if (!isRunning || !summary?.latestRunStartedAt || !job?.avgDurationSec) {
            return null;
          }

          const started = new Date(summary.latestRunStartedAt).getTime();

          if (Number.isNaN(started)) {
            return null;
          }

          const elapsedSec = Math.max(0, Math.floor((now - started) / 1000));

          return Math.max(0, job.avgDurationSec - elapsedSec);
        })();

        const thirdLabel = runningEtaSec !== null
          ? 'Finishes in'
          : nextEtaLabel ?? 'Next';
        const thirdValue = runningEtaSec !== null
          ? formatDurationCompact(runningEtaSec)
          : showCountdown ? formatCountdown(effective, now) : null;
        const thirdTone: 'warn' | undefined =
          runningEtaSec === null && !showCountdown ? 'warn' : undefined;

        return (
          <StatStrip>
            <StatTile>
              <StatLabel>
                <CheckCircleOutlined />
                {isRunning ? 'Started' : 'Last run'}
              </StatLabel>
              <StatValue>
                {summary?.latestRunStartedAt
                  ? formatRelative(summary.latestRunStartedAt)
                  : '—'}
              </StatValue>
            </StatTile>
            <StatTile>
              <StatLabel>
                <ClockCircleOutlined />
                {isRunning ? 'Avg duration' : 'Duration'}
              </StatLabel>
              <StatValue>
                {formatDurationCompact(
                  isRunning ? job?.avgDurationSec : summary?.latestRunDurationSec
                )}
              </StatValue>
            </StatTile>
            <StatTile>
              <StatLabel>
                <ClockCircleOutlined />
                {thirdLabel}
              </StatLabel>
              <StatValue $tone={thirdTone}>
                {thirdValue ?? '—'}
              </StatValue>
            </StatTile>
            {lagDays !== null && lagDays !== undefined && lagDays > 0 && (
              <LagChip style={{ gridColumn: '1 / -1', marginLeft: 0 }}>
                <ExclamationCircleOutlined />
                {lagDays}d behind upstream
              </LagChip>
            )}
          </StatStrip>
        );
      })()}
    </Card>
  );

  if (!hoverMessage) {
    return card;
  }

  const tooltipTone: 'warn' | 'bad' = tone === 'bad' ? 'bad' : 'warn';
  const palette = tooltipTone === 'bad'
    ? { bg: '#fff1f0', border: '#ffa39e', text: '#a8071a', icon: '#cf1322' }
    : { bg: '#fffbe6', border: '#ffe58f', text: '#874d00', icon: '#d48806' };

  const tooltipContent = (
    <TooltipContent $palette={palette}>
      <TooltipHeader>
        <ExclamationCircleOutlined style={{ color: palette.icon, fontSize: 14 }} />
        <span>{tooltipTone === 'bad' ? 'Failure' : 'Soft warning'}</span>
      </TooltipHeader>
      <TooltipBody>{hoverMessage}</TooltipBody>
    </TooltipContent>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement='bottom'
      color={palette.bg}
      overlayInnerStyle={{
        padding: 0,
        borderRadius: 8,
        border: `1px solid ${palette.border}`,
        boxShadow: '0 6px 20px rgba(0,0,0,0.08)'
      }}
      overlayStyle={{ maxWidth: 440 }}
    >
      {card}
    </Tooltip>
  );
};

interface IConnectorProps {
  accent: string;
  animated: boolean;
}

const Connector: React.FC<IConnectorProps> = ({ accent, animated }) => (
  <ConnectorSlot>
    <Rail $accent={accent}>
      <TravelDot $accent={accent} $duration={animated ? '1.6s' : '4s'} />
    </Rail>
    <Chevron $accent={accent}>▶</Chevron>
  </ConnectorSlot>
);

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

const BookstatDataFlow: React.FC = () => {
  const { summaries } = useDashboardCards();
  const { data } = useBookstatDataFlow();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);

    return () => window.clearInterval(id);
  }, []);

  const inferenceSummary = useMemo(
    () => summaries.find((s) => s.id === INFERENCE_ID),
    [summaries]
  );
  const backendSummary = useMemo(
    () => summaries.find((s) => s.id === BACKEND_ID),
    [summaries]
  );

  const inferenceJob = data?.data.inference ?? null;
  const backendJob = data?.data.backend ?? null;
  const lagDays = daysBetween(inferenceJob?.bookstatDate, backendJob?.bookstatDate);

  const backendCaughtUp = lagDays !== null && lagDays <= 0;
  const backendEta = backendCaughtUp
    ? inferenceSummary?.nextExpectedAt ?? null
    : backendSummary?.nextExpectedAt ?? null;
  const backendEtaLabel = backendCaughtUp ? 'Waiting on inference, retrying in ' : 'Next pickup';

  const inferenceColor = STATUS_COLOR[inferenceSummary?.latestRunStatus ?? 'unknown'];

  // Inference graceful-exits when upstream Bookstat data isn't ready —
  // hourly cron still fires, but each tick no-ops. Show "Waiting on
  // Bookstat" instead of a misleading countdown.
  const inferenceWaitingOnBookstat =
    inferenceSummary?.latestRunStatus === 'warning'
    && (/not yet available/i).test(inferenceSummary?.errorMessage ?? '');

  const c1Animated =
    inferenceSummary?.latestRunStatus === 'running' ||
    backendSummary?.latestRunStatus === 'running' ||
    (lagDays !== null && lagDays > 0);

  const overallStatus = backendSummary?.latestRunStatus ?? 'unknown';
  const overallTone: Tone = statusToTone(overallStatus, lagDays);
  const overallColor = STATUS_COLOR[overallStatus];

  const headlineDate = formatDateOnly(backendJob?.bookstatDate);
  const headlineSuffix =
    lagDays !== null && lagDays > 0
      ? ` · ${lagDays}d behind upstream`
      : '';

  return (
    <Wrapper>
      <Header>
        <TitleBlock>
          <Title>
            <ThunderboltFilled />
            Bookstat Data Flow
          </Title>
          <TitleHint>Inference → Backend · pipeline freshness</TitleHint>
        </TitleBlock>
        <FreshnessPill $tone={overallTone}>
          <FreshnessDot $color={overallColor} />
          Latest data is as of {headlineDate}{headlineSuffix}
        </FreshnessPill>
      </Header>

      <FlowRow>
        <FlowCard
          title='Inference Pipeline'
          icon={<ApiOutlined />}
          summary={inferenceSummary}
          job={inferenceJob}
          now={now}
          nextEtaOverride={inferenceWaitingOnBookstat ? null : undefined}
          nextEtaLabel={inferenceWaitingOnBookstat ? 'Waiting on Bookstat' : undefined}
        />
        <Connector accent={inferenceColor} animated={c1Animated} />
        <FlowCard
          title='Backend Data Refresh'
          icon={<DatabaseOutlined />}
          summary={backendSummary}
          job={backendJob}
          now={now}
          lagDays={lagDays}
          nextEtaOverride={backendEta}
          nextEtaLabel={backendEtaLabel}
        />
      </FlowRow>
    </Wrapper>
  );
};

export default BookstatDataFlow;
